import { useCallback, useEffect, useState } from "react";

import { endOfDay, startOfDay } from "date-fns";
import { DateRange } from "react-day-picker";
import { useSearchParams } from "react-router-dom";
import { useSessionStorage } from "usehooks-ts";

import { DecisionHistoryKeys } from "src/router/SearchParams";

export type StrictDateRange = {
  from: Date;
  to: Date;
};

export const useTimeWindowQueryParamsOnce = (): StrictDateRange | null => {
  const [params, setParams] = useSearchParams();
  const [from, to] = [
    params.get(DecisionHistoryKeys.From),
    params.get(DecisionHistoryKeys.To),
  ];

  if (from && to) {
    // clear the from and to once loaded
    // otherwise the data picker gets frozen to the URL params
    // but we use session storage to be the authority.
    setParams((prev) => {
      prev.delete(DecisionHistoryKeys.From);
      prev.delete(DecisionHistoryKeys.To);
      return prev;
    });
    return {
      from: startOfDay(new Date(from)),
      to: endOfDay(new Date(to)),
    };
  }

  return null;
};

/*
This is a helper to make the state management of the
date range picker easier. With that component its possible
to have an "incomplete" state (the user has selected the start but not the end i.e.)
which is unconvenient for the query consuming the state.

In this hook we manage two states:
1. Picker value: The state of the UI component
2. Time window: What the query consumes, always of type StrictDateRange
*/
export const useTimeWindow = <T extends StrictDateRange | undefined>(
  baseTimeWindow: T,
  sessionStorageKey: string,
  propagateUpdates: boolean = false,
) => {
  const [timeWindow, _setTimeWindow, _resetTimeWindow] = useSessionStorage<T>(
    sessionStorageKey,
    baseTimeWindow,
    {
      serializer: (value) => JSON.stringify(value),
      deserializer: (value) => {
        if (!value || value === "undefined") return undefined as T;
        const parsedObject = JSON.parse(value);
        return {
          from: new Date(parsedObject.from),
          to: new Date(parsedObject.to),
        } as T;
      },
    },
  );

  const [dateRangePickerValue, _setPickerValue] = useState<
    DateRange | undefined
  >(timeWindow);

  const onDateRangePickerChange = useCallback(
    (value?: DateRange) => {
      // Always update the UI state because the component
      // allows incomplete states
      _setPickerValue(value);

      // If the state is complete we change time window too
      if (!!value && !!value.from && !!value.to)
        _setTimeWindow({
          from: startOfDay(value.from),
          to: endOfDay(value.to),
        } as T);
    },
    [_setPickerValue, _setTimeWindow],
  );

  const onDateRangePickerReset = useCallback(() => {
    _setPickerValue(undefined);
    _setTimeWindow(undefined as T);
  }, [_setPickerValue, _setTimeWindow]);

  useEffect(() => {
    if (propagateUpdates) {
      onDateRangePickerChange(baseTimeWindow);
    }
  }, [
    baseTimeWindow,
    _setTimeWindow,
    onDateRangePickerChange,
    propagateUpdates,
  ]);

  return {
    timeWindow,
    dateRangePickerValue,
    onDateRangePickerChange,
    onDateRangePickerReset,
  };
};
